/**
 * What a locked browser can still say honestly (lib/content-lock.ts). Nothing here is a count of
 * encrypted work: those read "—" while locked.
 *
 * - `folded`: Needs-you rows that exist only because this browser cannot read the machine's
 *   encrypted report (opaque stalls: "Waiting for a fresh report … Unlock this browser"). They are
 *   a consequence of the lock, shown under the lock banner, never as separate blockers.
 * - `other`: rows that stay true while locked (device alerts, landing failures…).
 * - `machines`: runs in use per online machine, from the machine's own report (caps): the one
 *   source for "running" when this browser cannot list the runs themselves.
 */
import { ago, isBlockerItem } from './inbox-freshness.js'
import type { InboxRow } from './now-inbox.js'
import { SCHEDULE_OPAQUE_TITLE } from './stall-text.js'
import type { Machine } from './types.js'

export interface MachineLoad {
  id: string
  name: string
  running: number
  maxRuns: number
}

export function isLockConsequence(row: Pick<InboxRow, 'item'>): boolean {
  const item = row.item
  if (!item || !isBlockerItem(item)) return false
  return (item.body as { title?: unknown } | undefined)?.title === SCHEDULE_OPAQUE_TITLE
}

export function lockedView(
  rows: readonly InboxRow[],
  machines: readonly Pick<Machine, 'id' | 'name' | 'online' | 'caps'>[],
) {
  const folded = rows.filter(isLockConsequence)
  const other = rows.filter((r) => !isLockConsequence(r))
  const load: MachineLoad[] = machines
    .filter((m) => m.online && m.caps && m.caps.running > 0)
    .map((m) => ({
      id: m.id,
      name: m.name,
      running: m.caps!.running,
      maxRuns: m.caps!.maxRuns,
    }))
  return { folded, other, machines: load, running: load.reduce((n, m) => n + m.running, 0) }
}

/** "1 of 4 runs in use on Areks-Macbook-Pro.local". */
export function loadLine(m: MachineLoad): string {
  return `${m.running} of ${m.maxRuns} runs in use on ${m.name}`
}

/** "Areks-Macbook-Pro.local" → "Areks MacBook Pro" (the hostname stays in the title tooltip). */
export function friendlyMachineName(name: string): string {
  const base = name.replace(/\.(local|lan|home|localdomain)$/i, '')
  if (!/^[\w-]+$/.test(base) || !base.includes('-')) return base || name
  return base
    .split('-')
    .filter(Boolean)
    .map((w) => (/^macbook$/i.test(w) ? 'MacBook' : /^imac$/i.test(w) ? 'iMac' : w))
    .join(' ')
}

/** "your Mac" / "your PC" / "your computer", from the machine's platform. */
export function machineNoun(platform: string | null | undefined): string {
  if (platform === 'mac') return 'your Mac'
  if (platform === 'windows') return 'your PC'
  return 'your computer'
}

/** "this phone" on a phone, else "this browser". */
export function deviceNoun(ua = globalThis.navigator?.userAgent ?? ''): string {
  return /iPhone|iPod|Android.+Mobile|Windows Phone/i.test(ua) ? 'this phone' : 'this browser'
}

export interface HolderView {
  machineId: string
  name: string
  hostname: string
  noun: string
  online: boolean
  /** "Online" / "Offline · last seen 11 h ago". */
  state: string
  /** What to do about it, when there is something to do. */
  hint?: string
}

/** A key-holding machine, as the locked panel lists it (never "no machine" when one is online). */
export function holderView(
  h: { machineId: string; name: string; online: boolean; lastSeenAt?: number },
  platform: string | null | undefined,
  now: number,
): HolderView {
  const noun = machineNoun(platform)
  const Noun = noun.replace(/^your/, 'Your')
  return {
    machineId: h.machineId,
    name: friendlyMachineName(h.name),
    hostname: h.name,
    noun,
    online: h.online,
    state: h.online
      ? 'Online'
      : h.lastSeenAt
        ? `Offline · last seen ${ago(now - h.lastSeenAt)}`
        : 'Offline',
    ...(h.online
      ? {}
      : {
          hint:
            platform === 'mac'
              ? `Open ${noun}: Viko starts automatically.`
              : `${Noun} needs to be on, with Viko running.`,
        }),
  }
}
